import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { ReconciliationNoteButton } from "@/components/ReconciliationNoteButton";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, TrendingUp, TrendingDown, Wallet, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

const currency = (n) => {
  const num = Number(n || 0);
  const sign = num < 0 ? "-" : "";
  return `${sign}₹${Math.abs(num).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};
const EMPTY_TXN = { txn_date: "", type: "Income", category: "", description: "", amount: "" };

export default function FinancialOverview() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_TXN);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/financial-overview/summary").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitTxn = async () => {
    if (!form.txn_date || !form.description.trim() || !form.amount) {
      toast.error("Date, description, and amount are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/financial-overview/transactions", { ...form, amount: Number(form.amount), category: form.category || "General" });
      toast.success("Entry recorded");
      setFormOpen(false);
      setForm(EMPTY_TXN);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return <DashboardLayout title="Financial Overview"><p className="text-sm text-[#4B5563]">Loading...</p></DashboardLayout>;

  return (
    <DashboardLayout title="Financial Overview">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="financial-overview-summary-cards">
        <SummaryCard icon={Wallet} label="Current Balance" value={currency(data.current_balance)} testId="card-current-balance" accent={data.current_balance >= 0 ? "text-[#00823B]" : "text-red-600"} />
        <SummaryCard icon={ArrowDownToLine} label="Total Receivables" value={currency(data.total_receivables)} testId="card-total-receivables" accent="text-[#0EA5A0]" />
        <SummaryCard icon={ArrowUpFromLine} label="Total Payables" value={currency(data.total_payables)} testId="card-total-payables" accent="text-[#B45309]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="receivables-panel">
          <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#0EA5A0]" /><p className="text-sm font-semibold text-[#1C1C1E]">Receivables (Unpaid Client Invoices)</p>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {data.receivables.length === 0 && <tr><td className="px-4 py-6 text-center text-[#4B5563]">No outstanding receivables.</td></tr>}
                {data.receivables.map((r) => (
                  <tr key={r.id} data-testid={`receivable-row-${r.id}`} className="border-b border-[#E2E8F0] last:border-b-0">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-[#1C1C1E]">{r.client_name}</p>
                      <p className="text-xs text-[#4B5563]">{r.invoice_number} · Due {r.due_date}</p>
                    </td>
                    <td className="px-4 py-2.5"><StatusBadge value={r.status} /></td>
                    <td className="px-4 py-2.5 text-right font-medium">{currency(r.amount)}</td>
                    <td className="px-2 py-2.5"><ReconciliationNoteButton entityType="invoice" entityId={r.id} label={r.invoice_number} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="payables-panel">
          <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-[#B45309]" /><p className="text-sm font-semibold text-[#1C1C1E]">Payables (Unpaid Purchase Invoices)</p>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {data.payables.length === 0 && <tr><td className="px-4 py-6 text-center text-[#4B5563]">No outstanding payables.</td></tr>}
                {data.payables.map((p) => (
                  <tr key={p.id} data-testid={`payable-row-${p.id}`} className="border-b border-[#E2E8F0] last:border-b-0">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-[#1C1C1E]">{p.vendor_name}</p>
                      <p className="text-xs text-[#4B5563]">{p.invoice_number} · Due {p.due_date || "—"}</p>
                    </td>
                    <td className="px-4 py-2.5"><StatusBadge value={p.status} /></td>
                    <td className="px-4 py-2.5 text-right font-medium">{currency(p.amount)}</td>
                    <td className="px-2 py-2.5"><ReconciliationNoteButton entityType="purchase_invoice" entityId={p.id} label={p.invoice_number} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="running-ledger-panel">
        <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1C1C1E]">Income / Expense Ledger — Running Balance</p>
          {isAdmin && (
            <Button size="sm" onClick={() => setFormOpen(true)} data-testid="add-ledger-transaction-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Entry
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium text-right">Running Balance</th>
                <th className="px-4 py-3 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.ledger_rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#4B5563]">No ledger activity yet.</td></tr>}
              {data.ledger_rows.map((row, idx) => (
                <tr key={idx} data-testid={`ledger-row-${idx}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA]">
                  <td className="px-4 py-2.5 text-[#4B5563] whitespace-nowrap">{row.date}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={row.type} /></td>
                  <td className="px-4 py-2.5 text-[#4B5563]">{row.source}</td>
                  <td className="px-4 py-2.5 text-[#1C1C1E]">{row.description}</td>
                  <td className={`px-4 py-2.5 text-right font-medium ${row.type === "Income" ? "text-[#00823B]" : "text-red-600"}`}>
                    {row.type === "Income" ? "+" : "-"}{currency(row.amount)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-[#1C1C1E]">{currency(row.running_balance)}</td>
                  <td className="px-4 py-2.5"><ReconciliationNoteButton entityType={row.entity_type} entityId={row.entity_id} label={row.description} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ledger-transaction-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add Manual Ledger Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input data-testid="ledger-txn-date-input" type="date" value={form.txn_date} onChange={set("txn_date")} />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="ledger-txn-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Income">Income</SelectItem><SelectItem value="Expense">Expense</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input data-testid="ledger-txn-category-input" value={form.category} onChange={set("category")} placeholder="e.g. Bank Interest, Misc" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input data-testid="ledger-txn-description-input" value={form.description} onChange={set("description")} placeholder="Short description" />
            </div>
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input data-testid="ledger-txn-amount-input" type="number" min="0" value={form.amount} onChange={set("amount")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} data-testid="ledger-txn-cancel-button">Cancel</Button>
            <Button onClick={submitTxn} disabled={submitting} data-testid="ledger-txn-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
              {submitting ? "Saving..." : "Save Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, testId, accent }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-start justify-between">
      <div>
        <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-1">{label}</p>
        <p className={`text-xl font-bold ${accent || "text-[#1C1C1E]"}`}>{value}</p>
      </div>
      <div className="h-9 w-9 rounded-md bg-[#76C121]/10 flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5 text-[#00823B]" />
      </div>
    </div>
  );
}
